import * as React from "react";
import { useTranslation } from "react-i18next";
import { useIsSmallScreen } from "../hooks/useIsSmallScreen";
import { ComputerDesktopIcon } from "./icons";

// Persisted per browser session so "Continue anyway" survives resize/rotation
// but resets on the next session. sessionStorage (not localStorage) is
// intentional — we want to nudge users back to a computer each visit.
const DISMISS_KEY = "smallScreenOverlayDismissed";

/**
 * Full-screen blocking overlay shown when the viewport is too small for the
 * desktop-first studio UI. Mounted once globally (see `src/index.tsx`) so it
 * covers every route and auth state. Users can bypass it via "Continue
 * anyway", which hides it for the rest of the session.
 */
export const SmallScreenOverlay: React.FC = () => {
  const { t } = useTranslation();
  const isSmall = useIsSmallScreen();
  const [dismissed, setDismissed] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === "true";
    } catch {
      return false;
    }
  });

  const handleContinue = React.useCallback(() => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // sessionStorage can throw in private-mode/quota edge cases — still hide
      // the overlay for this render so the user is never stuck behind it.
    }
    setDismissed(true);
  }, []);

  if (!isSmall || dismissed) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="small-screen-overlay-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-50 px-6 text-center"
    >
      <div className="flex max-w-sm flex-col items-center gap-5">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-900/5">
          <ComputerDesktopIcon className="h-8 w-8 text-gray-700" />
        </div>
        <h1
          id="small-screen-overlay-title"
          className="text-xl font-semibold text-gray-900"
        >
          {t("smallScreenOverlay.title")}
        </h1>
        <p className="text-sm leading-relaxed text-gray-600">
          {t("smallScreenOverlay.description")}
        </p>
        <button
          type="button"
          onClick={handleContinue}
          className="text-sm font-medium text-gray-400 underline-offset-4 transition-colors hover:text-gray-600 hover:underline"
        >
          {t("smallScreenOverlay.continueAnyway")}
        </button>
      </div>
    </div>
  );
};
